import React, { useEffect, useState } from "react";
import api, { API_BASE, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, X, Phone, Mail } from "lucide-react";

/**
 * Fullscreen printable sheet. Each locker is rendered as a PAIR of cards:
 * a CUSTOMER COPY (handed to the customer) and a LUGGAGE TAG (stays with
 * the bag). Uses @media print CSS (App.css) so only the cards print.
 */
function Card({ locker, tenant, copyLabel }) {
  const brandName = tenant?.name || "Abrah Supermarket Ltd";
  return (
    <div className="lug-card border border-gray-400 rounded-lg bg-white overflow-hidden flex flex-col"
      style={{ width: "85mm", height: "54mm" }}>
      {/* Brand header */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[#022C22] text-white">
        <div className="h-6 w-6 rounded bg-white flex items-center justify-center overflow-hidden shrink-0">
          {tenant?.logo_file_id ? (
            <img src={fileUrl(tenant.logo_file_id)} alt="logo" className="h-full w-full object-contain" />
          ) : /abrah/i.test(brandName) ? (
            <img src="/abrah-logo.jpeg" alt="Abrah Supermarket logo" className="h-full w-full object-contain" data-testid="lug-card-default-logo" />
          ) : (
            <span className="text-[#022C22] font-bold text-sm">{brandName.slice(0, 1)}</span>
          )}
        </div>
        <div className="leading-tight">
          <div className="text-[10px] font-bold">{brandName}</div>
          <div className="text-[7px] uppercase tracking-[0.18em] text-emerald-200">Luggage entry card · {copyLabel}</div>
        </div>
        <div className="ml-auto text-[8px] text-emerald-200">{locker.branch}</div>
      </div>
      {/* Body */}
      <div className="flex items-center gap-3 px-3 flex-1">
        <div className="flex-1">
          <div className="text-[8px] uppercase tracking-[0.16em] text-gray-500">Card number</div>
          <div className="font-display text-3xl font-extrabold text-emerald-900 leading-none mt-0.5">
            {locker.locker_no}
          </div>
          <div className="text-[8px] text-gray-500 mt-1.5 leading-snug">
            Keep this card safe. Present it to collect your luggage.
          </div>
        </div>
        <img src={`${API_BASE}/luggage/lockers/${locker.id}/qr.png`} alt={`QR ${locker.locker_no}`}
          className="h-[26mm] w-[26mm] object-contain shrink-0" />
      </div>
      {/* Contact footer */}
      <div className="px-3 py-1 border-t border-gray-200 bg-gray-50 flex items-center gap-3 text-[8px] text-gray-600">
        <span className="font-semibold text-gray-700">Lost / found?</span>
        {locker.contact_phone && (
          <span className="inline-flex items-center gap-1"><Phone size={8} /> {locker.contact_phone}</span>
        )}
        {locker.contact_email && (
          <span className="inline-flex items-center gap-1"><Mail size={8} /> {locker.contact_email}</span>
        )}
      </div>
    </div>
  );
}

export default function LuggagePrintSheet({ lockers, onClose }) {
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    api.get("/tenants/me").then(({ data }) => setTenant(data)).catch(() => {});
    document.body.classList.add("printing-cards");
    return () => document.body.classList.remove("printing-cards");
  }, []);

  return (
    <div className="fixed inset-0 z-[80] bg-gray-100 overflow-y-auto" data-testid="lug-print-sheet">
      {/* Toolbar (hidden when printing) */}
      <div className="print-hide sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <div className="text-sm font-semibold text-gray-800">
          Print preview — {lockers.length} locker(s) × 2 copies (customer + luggage tag)
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => window.print()}
            className="bg-emerald-900 hover:bg-emerald-800" data-testid="lug-do-print">
            <Printer size={14} className="mr-1.5" /> Print
          </Button>
          <Button size="sm" variant="outline" onClick={onClose} data-testid="lug-close-print">
            <X size={14} className="mr-1.5" /> Close
          </Button>
        </div>
      </div>
      {/* Cards */}
      <div className="print-area p-6 flex flex-wrap gap-4 justify-center bg-white">
        {lockers.map((l) => (
          <React.Fragment key={l.id}>
            <Card locker={l} tenant={tenant} copyLabel="Customer copy" />
            <Card locker={l} tenant={tenant} copyLabel="Luggage tag" />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
